import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Toaster } from "./components/ui/toaster";
import { checkAuthState } from "./redux/slices/authSlice";
import { fetchLanguages } from "./redux/slices/languageSlice";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";
import AuthLayout from "./components/layout/AuthLayout";

// Pages
import Login from "./pages/auth/Login";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import UserDetails from "./pages/UserDetails";
import Analytics from "./pages/Analytics";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import NotFound from "./pages/NotFound";

// Quiz Management
import Categories from "./pages/categories/Categories";
import Questions from "./pages/questions/Questions";
import Languages from "./pages/languages/Languages";
import Locations from "./pages/locations/Locations";
import MultilingualStrings from "./pages/multilingual-strings/MultilingualStrings";

import DailyQuizzes from "./pages/daily-quizzes";
import Courses from "./pages/courses/Courses";
import CourseDetail from "./pages/courses/CourseDetail";
import CertificateTemplates from "./pages/certificate-templates/CertificateTemplates";
import CertificateTemplateEditor from "./pages/certificate-templates/CertificateTemplateEditor";
import { ContentPage, FilesPage } from "./pages/cms";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useSelector((state) => state.auth);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

function App() {
  const dispatch = useDispatch();
  const authChecked = React.useRef(false);
  const languagesFetched = React.useRef(false);

  useEffect(() => {
    if (!authChecked.current) {
      authChecked.current = true;
      dispatch(checkAuthState());
    }
  }, [dispatch]);

  useEffect(() => {
    // Fetch languages once on app load for MultilingualInput component
    if (!languagesFetched.current) {
      languagesFetched.current = true;
      dispatch(fetchLanguages());
    }
  }, [dispatch]);

  return (
    <>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
        </Route>

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/:id" element={<UserDetails />} />

          {/* Quiz Management */}
          <Route path="/categories" element={<Categories />} />
          <Route path="/questions" element={<Questions />} />
          <Route path="/daily-quizzes" element={<DailyQuizzes />} />

          {/* Other Routes */}
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/certificate-templates" element={<CertificateTemplates />} />
          <Route
            path="/certificate-templates/:id"
            element={<CertificateTemplateEditor />}
          />
          <Route
            path="/certificates"
            element={<Navigate to="/certificate-templates" replace />}
          />
          <Route path="/languages" element={<Languages />} />
          <Route path="/locations" element={<Locations />} />
          <Route
            path="/multilingual-strings"
            element={<MultilingualStrings />}
          />

          <Route path="/content" element={<ContentPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settings" element={<Settings />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <Toaster />
    </>
  );
}

export default App;
